import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import { ChevronRight, ChevronsUpDown } from "lucide-mui";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import Paper from "@mui/material/Paper";
import Popper, { type PopperPlacementType } from "@mui/material/Popper";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import { rowPaddingFor } from "../layouts/sidebar/styles";
import { floatingSurface } from "../theme";

export const CONTEXT_FLYOUT_WIDTH = 260;

/** Marks a row, so another row's flyout leaves the click to it. */
const ROW_ATTR = "data-context-row";

interface ContextRowProps {
  /** Small uppercase kicker rendered above the label (e.g. the org name) */
  kicker?: string;
  /** Muted line rendered below the label (e.g. an email) */
  secondary?: string;
  /** Icon or avatar shown at the left of the row */
  icon: ReactNode;
  /** Width of `icon` in px; centres it in the collapsed rail */
  iconSize?: number;
  /** Primary label (e.g. the tenant name) */
  label: string;
  /** When true, the primary sidebar is collapsed — render icon-only */
  collapsed: boolean;
  /** Content rendered inside the flyout popper */
  flyoutContent: ReactNode;
  /** Unique identifier for this flyout, used for single-open coordination */
  flyoutId: string;
  /** The flyout id currently pinned by the parent (null = none) */
  pinnedFlyoutId: string | null;
  /** Setter for the pinned flyout id, owned by the parent */
  setPinnedFlyoutId: (id: string | null) => void;
  /** `switcher` for a context picker (up/down caret), `menu` for a submenu */
  variant?: "switcher" | "menu";
  /** Where the flyout opens; rows at the bottom of the sidebar open upwards */
  placement?: PopperPlacementType;
  flyoutWidth?: number;
  /** Highlight the row, e.g. while the current page lives in its flyout */
  selected?: boolean;
  /** When true, render skeleton placeholders for icon/label and disable interaction */
  loading?: boolean;
}

/**
 * A sidebar row that opens a flyout: the tenant switcher and the Explore,
 * Platform and account menus. Clicking opens it; once one is open, hovering
 * another row moves to that one (like a menu bar). ClickAway or Escape closes.
 * Single-open coordination is delegated to the parent via `pinnedFlyoutId`.
 */
export function ContextRow({
  kicker,
  secondary,
  icon,
  iconSize = 18,
  label,
  collapsed,
  flyoutContent,
  flyoutId,
  pinnedFlyoutId,
  setPinnedFlyoutId,
  variant = "menu",
  placement = "right-start",
  flyoutWidth = CONTEXT_FLYOUT_WIDTH,
  selected = false,
  loading = false,
}: ContextRowProps) {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const open = !loading && pinnedFlyoutId === flyoutId;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setPinnedFlyoutId(null);
      anchorRef.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, setPinnedFlyoutId]);

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    // A click on a row (this one included) is that row's own toggle.
    if ((event.target as HTMLElement | null)?.closest(`[${ROW_ATTR}]`)) return;
    if (open) setPinnedFlyoutId(null);
  };

  const Caret = variant === "switcher" ? ChevronsUpDown : ChevronRight;

  const rowButton = (
    <ListItemButton
      ref={anchorRef as unknown as React.Ref<HTMLDivElement>}
      {...{ [ROW_ATTR]: "" }}
      aria-expanded={open}
      aria-label={collapsed ? (kicker ? `${kicker}: ${label}` : label) : undefined}
      onClick={() => {
        if (!loading) setPinnedFlyoutId(open ? null : flyoutId);
      }}
      onMouseEnter={() => {
        if (!loading && pinnedFlyoutId !== null && pinnedFlyoutId !== flyoutId) {
          setPinnedFlyoutId(flyoutId);
        }
      }}
      selected={selected || open}
      sx={{
        "--row-px": rowPaddingFor(iconSize),
        "--row-py": kicker || secondary ? "6px" : undefined,
        pointerEvents: loading ? "none" : undefined,
      }}
    >
      <ListItemIcon>
        {loading ? <Skeleton variant="rounded" width={iconSize} height={iconSize} /> : icon}
      </ListItemIcon>
      {!collapsed && (
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          {kicker && (
            <Box
              component="span"
              sx={{
                fontSize: "0.625rem",
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "text.secondary",
                lineHeight: 1.4,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {kicker}
            </Box>
          )}
          {loading ? (
            <Skeleton variant="text" width="70%" sx={{ fontSize: "0.8125rem" }} />
          ) : (
            <Box
              component="span"
              sx={{
                fontSize: "0.8125rem",
                fontWeight: kicker || secondary ? 600 : 500,
                color: selected || open ? "inherit" : "text.primary",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.35,
              }}
            >
              {label}
            </Box>
          )}
          {secondary && (
            <Box
              component="span"
              sx={{
                fontSize: "0.6875rem",
                color: "text.secondary",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.35,
              }}
            >
              {secondary}
            </Box>
          )}
        </Box>
      )}
      {!collapsed && !loading && (
        <Caret sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0, ml: "auto" }} />
      )}
    </ListItemButton>
  );

  const bottomUp = placement.endsWith("-end");

  return (
    <>
      {/* Always wrapped (an empty title never shows), so opening doesn't remount
          the row and leave the popper anchored to a detached node. */}
      <Tooltip
        title={collapsed && !open && !loading ? (kicker ? `${kicker}: ${label}` : label) : ""}
        placement="right"
        disableInteractive
      >
        {rowButton}
      </Tooltip>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement={placement}
        modifiers={[{ name: "offset", options: { offset: [bottomUp ? 4 : -4, 12] } }]}
        sx={{ zIndex: (t) => t.zIndex.drawer + 2 }}
        transition
      >
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            timeout={150}
            style={{ transformOrigin: bottomUp ? "left bottom" : "left top" }}
          >
            <Paper
              elevation={0}
              sx={[
                floatingSurface,
                {
                  width: flyoutWidth,
                  overflow: "hidden",
                  maxHeight: "calc(100vh - 32px)",
                  display: "flex",
                  flexDirection: "column",
                },
              ]}
            >
              <ClickAwayListener onClickAway={handleClickAway}>
                <Box sx={{ overflow: "auto" }}>{flyoutContent}</Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}

export default ContextRow;
